import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, bowser, dialogs } from '@yola/ws-sdk';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import StockGalleryDialog from '../components/stock-gallery-dialog';
import trackers from '../trackers';
import dialogsTypes from '../../dialogs/constants/dialog-types';
import useSearchQuery from '../hooks/use-search-query';
import stockIds from '../constants/stock-ids';
import stockLabels from '../constants/stock-labels';

const { trackPhotoStockSearchClicked, trackPhotoStockSearchPerformed } = trackers;
const { PHOTO_LOAD_ERROR } = dialogsTypes;

const getTabs = ({ isIstockImageBrowserEnabled }) => {
  const tabs = [
    {
      id: stockIds.UNSPLASH,
      label: stockLabels.UNSPLASH,
    },
    ...(isIstockImageBrowserEnabled
      ? [
          {
            id: stockIds.ISTOCK,
            label: stockLabels.ISTOCK,
          },
        ]
      : []),
  ];

  return tabs;
};

function StockGalleryContainer({
  blockId,
  multiple = false,
  preserveSharedData = false,
  onDialogMainAction,
  onDialogCancel,
}) {
  const isMobile = Boolean(bowser.mobile || bowser.tablet);

  const [featureFlags] = useFeatureFlags(['istock_image_browser']);
  const { istock_image_browser: isIstockImageBrowserEnabled } = featureFlags;

  const [activeTabId, setActiveTabId] = useState(stockIds.UNSPLASH);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState(null);

  const { getInitialSearchQuery, saveSearchQuery } = useSearchQuery();

  const tabs = useMemo(
    () => getTabs({ isIstockImageBrowserEnabled }),
    [isIstockImageBrowserEnabled]
  );

  const handleTabChange = useCallback((tabId) => {
    setActiveTabId(tabId);
    setSelectedItems([]);
  }, []);

  const handleCancel = useCallback(() => {
    onDialogCancel();
  }, [onDialogCancel]);

  const updateSelectedItems = useCallback((items) => {
    setSelectedItems(items);
  }, []);

  const updateSearchQuery = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleSearchFocus = useCallback(() => {
    trackPhotoStockSearchClicked({ targetElementId: blockId });
  }, [blockId]);

  const handleSearchEnd = useCallback(
    ({ searchTerm, searchResults }) => {
      trackPhotoStockSearchPerformed({
        targetElementId: blockId,
        searchTerm,
        searchResults,
      });
    },
    [blockId]
  );

  const handleLoadError = useCallback(
    (error) => {
      dialogs.operations.show(PHOTO_LOAD_ERROR, {
        preserveSharedData,
        isApiRateExceeded: Boolean(error && error.status === 429),
      });
    },
    [preserveSharedData]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedItems.length) {
      onDialogCancel();
      return;
    }

    if (activeTabId === stockIds.UNSPLASH) {
      saveSearchQuery(searchQuery);
    }

    const selectedPhotos = selectedItems.map(({ isSelected, isLoaded, ...rest }) => rest);

    onDialogMainAction({ stockId: activeTabId, selectedPhotos });
  }, [
    selectedItems,
    onDialogCancel,
    searchQuery,
    saveSearchQuery,
    onDialogMainAction,
    activeTabId,
  ]);

  return (
    <StockGalleryDialog
      tabs={tabs}
      activeTabId={activeTabId}
      captions={{
        title: i18next.t('Free stock photos library'),
        submit: i18next.t('Submit'),
        cancel: i18next.t('Cancel'),
      }}
      sharedSearchQuery={searchQuery}
      isMobile={isMobile}
      multiple={multiple}
      onSubmit={handleSubmit}
      onClose={handleCancel}
      onTabChange={handleTabChange}
      updateSelectedItems={updateSelectedItems}
      updateSearchQuery={updateSearchQuery}
      getInitialSearchQuery={getInitialSearchQuery}
      onSearchFocus={handleSearchFocus}
      onSearchEnd={handleSearchEnd}
      onLoadError={handleLoadError}
    />
  );
}

StockGalleryContainer.propTypes = {
  blockId: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  preserveSharedData: PropTypes.bool,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default StockGalleryContainer;
